import { getPost } from '@/lib/blog';
import '@/styles/landing.css';
import { createFileRoute, Link, notFound } from '@tanstack/react-router';

export const Route = createFileRoute('/blog/$slug')({
    loader: ({ params }) => {
        const post = getPost(params.slug);
        if (!post) throw notFound();
        return post;
    },
    head: ({ loaderData }) => ({
        meta: [
            {
                title: loaderData?.title
                    ? `${loaderData.title} | Stockwright`
                    : 'Stockwright',
            },
            { name: 'description', content: loaderData?.excerpt ?? '' },
            // Open Graph
            { property: 'og:title', content: loaderData?.title ?? '' },
            { property: 'og:description', content: loaderData?.excerpt ?? '' },
            { property: 'og:type', content: 'article' },
        ],
    }),
    component: BlogPostPage,
});

function BlogPostPage() {
    const post = Route.useLoaderData();

    return (
        <div className="sw-lp light">
            {/* Header / Nav */}
            <header className="lp-blog-header">
                <nav className="lp-blog-nav">
                    <Link to="/">
                        <img
                            src="https://res.cloudinary.com/flannery38/image/upload/v1771973120/stockwright/logo-white.svg"
                            alt="Stockwright"
                            className="h-8 lg:h-9 w-auto"
                            style={{
                                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))',
                            }}
                        />
                    </Link>
                    <div className="lp-nav-links">
                        <a href="/#features">What it does</a>
                        <a href="/#workflow">Workflow</a>
                        <a href="/#pricing">Pricing</a>
                        <a href="/#faq">FAQ</a>
                    </div>
                    <Link
                        to="/auth"
                        search={{ mode: 'signup' }}
                        className="lp-nav-cta"
                    >
                        Start free trial →
                    </Link>
                </nav>
            </header>

            {/* Post header */}
            <section className="lp-blog-intro">
                <div className="lp-container-narrow">
                    <Link to="/blog" className="lp-post-back">
                        ← Journal
                    </Link>
                    <div className="lp-eyebrow" style={{ marginTop: '20px' }}>
                        {post.category}
                    </div>
                    <h1 style={{ marginTop: '14px' }}>{post.title}</h1>
                    <p style={{ marginTop: '16px' }}>{post.excerpt}</p>
                    <div className="lp-post-byline">{post.date}</div>
                </div>
            </section>

            {/* Post body */}
            <article className="lp-blog-post-body">
                <div className="lp-container-narrow">
                    <div
                        className="lp-prose"
                        dangerouslySetInnerHTML={{ __html: post.html }}
                    />
                </div>
            </article>

            {/* Footer */}
            <footer className="lp-footer pt-16 px-10 pb-24">
                <div className="lp-container">
                    <div className="lp-footer-grid">
                        <div className="lp-footer-brand">
                            <img
                                src="https://res.cloudinary.com/flannery38/image/upload/v1771973120/stockwright/logo.svg"
                                alt="Stockwright"
                                loading="lazy"
                                decoding="async"
                            />
                            <p className="lp-footer-tag">
                                A calm, honest workspace for the resale business
                                you&rsquo;ve quietly been building. Sell more.
                                Stress less.
                            </p>
                        </div>
                        <div className="lp-footer-col">
                            <h5>Product</h5>
                            <a href="/#features">What it does</a>
                            <a href="/#workflow">Workflow</a>
                            <a href="/#integrations">Integrations</a>
                            <a href="/#pricing">Pricing</a>
                        </div>
                        <div className="lp-footer-col">
                            <h5>Company</h5>
                            <Link to="/blog">Journal</Link>
                        </div>
                        <div className="lp-footer-col">
                            <h5>Help</h5>
                            <Link to="/faq">FAQ</Link>
                            <Link to="/terms">Terms</Link>
                            <Link to="/security">Security</Link>
                        </div>
                    </div>
                    <div className="lp-footer-bottom">
                        <span>
                            &copy; {new Date().getFullYear()} Stockwright
                            &middot; Made for resellers
                        </span>
                        <span>Beta &middot; v0.5</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
